import security from "@/main";
import Vue from "vue";
import ImgFullscreen from "@/modules/img/components/main/ImgFullscreen";
import Core from "@/modules/core/service/Core";
import { getApiBaseUrl } from "@/config";
import { pickRendition } from "@/modules/img/components/service/Renditions";

// Media service (HCIT-1105)
const mediaUrl = {
    folders  : "/api/media/sites/{0}/folders",
    upload   : "/api/media/sites/{0}/files",
    inspectionFiles: "/api/media/inspections/{0}/files",
    uploadState: "/api/media/uploads/{0}",
    images   : "/api/media/sites/{0}/folders/{1}/images",
    rendition: "/api/media/files/{0}/renditions/{1}",
};

function postFile (path, file, onProgress) {
    const body = new FormData();
    body.append("file", file);

    // fetch can't report how much it sent
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest();
        request.open("POST", getApiBaseUrl() + path);
        const headers = security.header();
        Object.keys(headers).forEach(name => request.setRequestHeader(name, headers[name]));
        if (onProgress) {
            request.upload.onprogress = event => event.lengthComputable && onProgress(event.loaded / event.total);
        }
        request.onload = () => {
            if (request.status < 200 || request.status >= 300) {
                // seconds, if the server says how long to wait (Retry-After may also be a date: not used then)
                const retryAfter = parseInt(request.getResponseHeader("Retry-After"), 10);
                return reject({ status: request.status, retryAfter: isNaN(retryAfter) ? null : retryAfter });
            }
            try {
                resolve(JSON.parse(request.responseText).uploadId);
            } catch (e) {
                reject({ status: request.status });
            }
        };
        request.onerror = () => reject({ status: 0 });
        request.send(body);
    });
}

export default {
    /** The folders of a site: {folders}. */
    load (siteId) {
        return Core.method.get(mediaUrl.folders, siteId);
    },

    /** The images of a folder; each carries its renditions. */
    images (siteId, folder) {
        return Core.method.get(mediaUrl.images, siteId, folder.id);
    },

    /**
     * The URL of an image (an entry of a folder's images) for a purpose: "thumb", "view" or "full";
     * null if it has no rendition a browser can show.
     */
    imageUrl (image, purpose) {
        const rendition = pickRendition(image && image.renditions, purpose);
        return rendition ? getApiBaseUrl() + rendition.url : null;
    },

    /**
     * URLs to try, in order, for an image known only by its id (e.g. an inspection image): the display
     * size, then the original (a PDF or an image without variants).
     */
    imageUrlsById (imageId) {
        return ["display", "original"].map(name =>
            getApiBaseUrl() + mediaUrl.rendition.replace("{0}", imageId).replace("{1}", name));
    },

    /** Creates a folder in the media service; an existing folder of that name is returned again. */
    createFolder (siteId, name) {
        return Core.method.post(mediaUrl.folders, { name }, siteId);
    },

    /**
     * Uploads one image or PDF into a media service folder. [onProgress] gets the fraction (0..1) sent so
     * far. Resolves to the upload id once the media service accepted it (it is processed afterwards,
     * see uploadState); rejects with {status}.
     */
    upload (siteId, folderId, file, onProgress) {
        return postFile(mediaUrl.upload.replace("{0}", siteId) + "?folderId=" + encodeURIComponent(folderId), file, onProgress);
    },

    /**
     * All images of an inspection that are ready (status OK), each with its `imageType` (IMAGE or BOOK),
     * `linkStatus` and renditions. Filter by `imageType` on the client.
     */
    inspectionImages (inspectionId) {
        return Core.method.get(mediaUrl.inspectionFiles, inspectionId)
            .then(images => (images || []).filter(image => image.status === "OK"));
    },

    /**
     * Uploads one image or PDF to an inspection (HCIT-1107). [imageType] is what it is for, e.g. IMAGE or
     * BOOK. Like [upload], but the file shows up in the inspection's images only once disposition-svc
     * has released it, a moment after the upload state is DONE.
     */
    uploadToInspection (inspectionId, imageType, file, onProgress) {
        return postFile(mediaUrl.inspectionFiles.replace("{0}", inspectionId) + "?imageType=" + encodeURIComponent(imageType), file, onProgress);
    },

    /**
     * State of an upload: {status: IN_PROGRESS | DONE | FAILED, fileId, reason}. The media service
     * doesn't know an upload for the first seconds (404), which counts as IN_PROGRESS.
     */
    uploadState (uploadId) {
        return fetch(getApiBaseUrl() + mediaUrl.uploadState.replace("{0}", uploadId), {
            method : "GET",
            headers: security.jsonHeader(),
        }).then(response => {
            if (response.status === 404) {
                return { status: "IN_PROGRESS" };
            }
            return response.ok ? response.json() : Promise.reject(response);
        });
    },

    /** [image]: an entry of a folder's images. */
    fullscreen (component, image) {
        const header = security.header();
        const handle = window.open("", '_blank', "menubar=no,toolbar=no,location=no,status=no");
        const cmp = new Vue({...ImgFullscreen, });


        component.$nextTick(() => {
            cmp.$mount();
            cmp.$nextTick(() => {
                console.log("full")
                cmp.setToken(header);
                cmp.setImage(image);
                handle.document.body.appendChild (cmp.$el);
            })
        });
    }
}